import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

const getApiBaseUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL.replace(/\/$/, '');
  }
  if (
    typeof window !== 'undefined' &&
    (window.location.hostname.includes('ondigitalocean.app') ||
      window.location.hostname.includes('services.knls.in'))
  ) {
    return '/api';
  }
  return 'http://localhost:3000/api';
};

const API_BASE_URL = getApiBaseUrl();

const getAuthHeaders = () => {
  const token = localStorage.getItem('admin_token') || localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const parseTestResults = (value) => {
  if (Array.isArray(value)) return value;
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
};

export default function CodingReviewPage() {
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [awardedMarks, setAwardedMarks] = useState('');
  const [remarks, setRemarks] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const selected = useMemo(
    () => submissions.find((row) => row.id === selectedId) || submissions[0] || null,
    [submissions, selectedId],
  );

  useEffect(() => {
    loadSubmissions();
  }, []);

  useEffect(() => {
    if (!selected) return;
    setAwardedMarks(
      selected.awarded_marks ?? selected.awardedMarks ?? '',
    );
    setRemarks(selected.remarks || '');
  }, [selected]);

  const loadSubmissions = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`${API_BASE_URL}/lms/coding/submissions`, {
        headers: getAuthHeaders(),
        params: { status: 'all' },
      });
      const rows = response.data?.data || [];
      setSubmissions(rows);
      setSelectedId((prev) => prev || rows[0]?.id || null);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load coding submissions');
    } finally {
      setLoading(false);
    }
  };

  const saveReview = async () => {
    if (!selected) return;
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await axios.put(
        `${API_BASE_URL}/lms/coding/submissions/${selected.id}/review`,
        {
          awardedMarks: toNumber(awardedMarks),
          remarks,
        },
        { headers: getAuthHeaders() },
      );
      setSuccess('Review saved.');
      await loadSubmissions();
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to save review');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '60vh', display: 'grid', placeItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  const testResults = parseTestResults(selected?.test_results);

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Stack spacing={3}>
        <Box>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            justifyContent="space-between"
          >
            <Box>
              <Typography variant="h4" fontWeight={800}>
                Coding Review
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Auto-graded submissions appear here too. Override marks or add remarks when manual review is needed.
              </Typography>
            </Box>
            <Button
              variant="outlined"
              onClick={() => navigate('/dashboard/students/coding/problems')}
              sx={{ alignSelf: { xs: 'stretch', sm: 'flex-start' }, whiteSpace: 'nowrap' }}
            >
              Coding problems library
            </Button>
          </Stack>
        </Box>

        <Alert severity="info">
          Create reusable problems in the{' '}
          <strong>Coding Problems</strong> library, then attach them in{' '}
          <strong>Course Management → LMS Builder → Add Content → Coding Problem → Import from Library</strong>.
        </Alert>

        {error ? <Alert severity="error">{error}</Alert> : null}
        {success ? <Alert severity="success">{success}</Alert> : null}

        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Stack spacing={1.5}>
              {submissions.length === 0 ? (
                <Alert severity="info">No coding submissions yet.</Alert>
              ) : null}
              {submissions.map((row) => (
                <Card
                  key={row.id}
                  variant={selected?.id === row.id ? 'elevation' : 'outlined'}
                  sx={{ cursor: 'pointer' }}
                  onClick={() => setSelectedId(row.id)}
                >
                  <CardContent>
                    <Typography fontWeight={700}>{row.problem_title || 'Coding problem'}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {row.student_phone} • {row.language?.toUpperCase()}
                    </Typography>
                    <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
                      <Chip size="small" label={row.status} />
                      {row.auto_graded ? <Chip size="small" color="info" label="auto" /> : null}
                      <Chip
                        size="small"
                        color="success"
                        label={`${row.awarded_marks}/${row.total_marks}`}
                      />
                    </Stack>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          </Grid>

          <Grid item xs={12} md={8}>
            {!selected ? (
              <Alert severity="info">Select a submission to review.</Alert>
            ) : (
              <Stack spacing={2}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" fontWeight={700}>
                      {selected.problem_title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Student: {selected.student_phone} • Language: {selected.language}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      Grading mode: {selected.grading_mode || 'pending'}
                    </Typography>
                  </CardContent>
                </Card>

                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                      Submitted code
                    </Typography>
                    <Box
                      component="pre"
                      sx={{
                        m: 0,
                        p: 2,
                        bgcolor: '#0f172a',
                        color: '#e2e8f0',
                        borderRadius: 1,
                        overflow: 'auto',
                        fontFamily: 'ui-monospace, monospace',
                        fontSize: 13,
                      }}
                    >
                      {selected.source_code || '(empty)'}
                    </Box>
                  </CardContent>
                </Card>

                {testResults.length > 0 ? (
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                        Test results
                      </Typography>
                      <Stack spacing={1}>
                        {testResults.map((result, index) => (
                          <Alert
                            key={result.id || index}
                            severity={result.passed ? 'success' : 'error'}
                          >
                            Test {index + 1}: {result.passed ? 'PASS' : 'FAIL'}
                            {result.stderr ? ` — ${result.stderr}` : ''}
                          </Alert>
                        ))}
                      </Stack>
                    </CardContent>
                  </Card>
                ) : null}

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    label="Awarded marks"
                    type="number"
                    value={awardedMarks}
                    onChange={(e) => setAwardedMarks(e.target.value)}
                    inputProps={{ min: 0, max: toNumber(selected.total_marks), step: 0.5 }}
                    fullWidth
                  />
                  <TextField
                    label="Remarks"
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                    fullWidth
                    multiline
                    minRows={2}
                  />
                </Stack>

                <Button variant="contained" onClick={saveReview} disabled={saving}>
                  {saving ? 'Saving...' : 'Save review'}
                </Button>
              </Stack>
            )}
          </Grid>
        </Grid>
      </Stack>
    </Container>
  );
}
